<script lang="ts">
  // When CatalogueDetail.svelte mounts and finds no local sessionStorage
  // staged-edits state for the current (jurisdiction, product) but the
  // server reports an active draft branch for the same coordinate, this
  // prompt asks the operator whether to resume or discard the existing
  // draft.
  //
  // Without it, an active server-side draft from a prior session (e.g.
  // a closed browser tab, an identity switch, a stale draft from before
  // the slug-collision fix landed) would silently become the baseline
  // for the next saveDraft call — surfaced as the
  // proposed/au-unsecured-personal-loan-2026-05-21-001 incident where a
  // prior session's establishment_fee_override removal merged into a
  // rate-only proposal. Slug uniqueness now avoids the most common
  // collision shape; this prompt closes the residual gap when a
  // server-side draft outlives its in-memory store for any reason.

  import type { DraftSummary } from '../lib/types';
  import { relativeTimeAgo } from '../lib/formatters';

  interface Props {
    draft: DraftSummary;
    onResume: () => void;
    onDiscard: () => void;
    busy?: boolean;
  }

  const { draft, onResume, onDiscard, busy = false }: Props = $props();

  const lastEditedRelative = $derived(
    draft.last_edited_at ? relativeTimeAgo(draft.last_edited_at, Date.now()) : 'unknown'
  );
</script>

<aside class="draft-resume-prompt" aria-live="polite">
  <div class="draft-resume-prompt__body">
    <strong>You have an existing draft for this product.</strong>
    <span class="muted">
      <code>{draft.draft_slug}</code>
      · {draft.staged_change_count} staged change{draft.staged_change_count === 1 ? '' : 's'}
      · last edited {lastEditedRelative}
    </span>
    <p class="muted draft-resume-prompt__explainer">
      A draft from a prior session is alive on the server. Resume it to keep its
      staged changes, or discard it to archive that draft and start fresh from
      current main. Discard is the right call when the prior session's intent
      no longer matches what you want to ship.
    </p>
  </div>
  <div class="draft-resume-prompt__actions">
    <button class="btn btn-secondary" type="button" disabled={busy} onclick={onDiscard}>Discard and start fresh</button>
    <button class="btn btn-primary" type="button" disabled={busy} onclick={onResume}>Resume draft →</button>
  </div>
</aside>

<style>
  .draft-resume-prompt {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--accent-warn-bg, #fef3c7);
    border-left: 3px solid var(--accent-warn, #b45309);
    border-radius: var(--radius-md);
    padding: var(--space-3) var(--space-4);
    margin-bottom: var(--space-4);
  }
  .draft-resume-prompt__body {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--type-sm, 0.875rem);
  }
  .draft-resume-prompt__body code {
    font-family: var(--font-mono);
    font-size: 0.875em;
  }
  .draft-resume-prompt__explainer {
    margin: 0;
  }
  .draft-resume-prompt__actions {
    display: flex;
    gap: var(--space-3);
    justify-content: flex-end;
    flex-wrap: wrap;
  }
</style>
